import type {
  CivilAppellateSaveStep1,
  CivilAppellateSaveStep2,
  CivilAppellateSaveStep3,
  CivilAppellateSaveStep4,
  CivilAppellateSaveStep5,
  CivilAppellateSaveStep5File,
  CivilAppellateStepsResponse,
  SaveCivilAppellateStepsPayload,
} from '../types/civilAppellateApi';
import type {
  CivilAppellateJudgmentDocument,
  CivilAppellateWizardDraft,
} from '../types/civilAppellateCaseRequest';
import {
  draftHasOtherDeliverySelected,
} from '../utils/deliveryMethod';
import { isDocumentTypeUuid, isPredefinedDocumentType, resolveDocumentTypeUuid } from '../utils/documentTypeId';
import { caseRequestIdForSave, draftResumeStepFromApi, finalizeCaseRequestSavePayload, SaveStepFields, strOrNull } from '../utils/caseRequestPayload';
import { looksLikeStorageKey, normalizeDocumentFileFields } from '../utils/fileDisplayName';
import type { CivilAppellateWizardStep } from '../pages/wizard/civilAppellate/civilAppellateConstants';
import {
  CIVIL_APPELLATE_CATEGORY_ID,
  FILE_CATEGORY_CIVIL_APPELLATE,
} from '../pages/wizard/civilAppellate/civilAppellateConstants';
import {
  parseApplicableAreasFromApi,
  parsePhysicalFileDeliveryMethods,
} from './criminalCaseMapper';
import { buildInstituteApiPayload, mapInstituteFromApiResponse } from '../utils/instituteApiPayload';

export type { CivilAppellateWizardStep };

export function caseRequestIdFromResponse(response: CivilAppellateStepsResponse): string {
  const id = response.case_request_id ?? response.caseRequestId;
  return typeof id === 'string' ? id.trim() : '';
}

export function referenceNoFromResponse(response: CivilAppellateStepsResponse): string | undefined {
  const ref = response.reference_no ?? response.referenceNo ?? response.fileReferenceNumber;
  const t = typeof ref === 'string' ? ref.trim() : '';
  return t || undefined;
}

function step1LooksComplete(s1: CivilAppellateSaveStep1 | undefined): boolean {
  return Boolean(
    (s1?.sub_category_id ?? '').trim() &&
      (s1?.province_id ?? '').trim() &&
      (s1?.external_case_reference_number ?? '').trim(),
  );
}

function step2LooksComplete(s2: CivilAppellateSaveStep2 | undefined): boolean {
  return Boolean(
    (s2?.plaintiff_applicant_name ?? '').trim() && (s2?.defendant_respondant_name ?? s2?.defendant_respondent_name ?? '').trim(),
  );
}

function step3LooksComplete(s3: CivilAppellateSaveStep3 | undefined): boolean {
  return Boolean((s3?.urgency_level_id ?? '').trim());
}

function step4LooksComplete(s4: CivilAppellateSaveStep4 | undefined): boolean {
  return Boolean((s4?.physical_file_delivery_method_ids ?? []).filter(Boolean).length);
}

function step5LooksComplete(s5: CivilAppellateSaveStep5 | undefined): boolean {
  const files = step5DocumentsFromApi(s5);
  return files.some(
    (f) =>
      Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()) ||
      Boolean((f.reason ?? '').trim()),
  );
}

export function resolveCivilAppellateDraftResumeStep(
  response: CivilAppellateStepsResponse,
): CivilAppellateWizardStep {
  return draftResumeStepFromApi(
    response,
    () => {
  if (response.step1Processed === false) return 1;
  if (response.step2Processed === false) return 2;
  if (response.step3Processed === false) return 3;
  if (response.step4Processed === false) return 4;
  if (response.step5Processed === false) return 5;

  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;

  if (!step1LooksComplete(s1)) return 1;
  if (!step2LooksComplete(s2)) return 2;
  if (!step3LooksComplete(s3)) return 3;
  if (!step4LooksComplete(s4)) return 4;
  if (!step5LooksComplete(s5)) return 5;
  return 5;
    },
    (step) => step >= 1 && step <= 5,
  );
}

function legalOfficerNameFromStep2(s2: CivilAppellateSaveStep2 | undefined): string | undefined {
  return s2?.legal_office_or_officer_incharge_name ?? s2?.leagel_office_or_officer_incharge_name;
}

function legalOfficerContactFromStep2(s2: CivilAppellateSaveStep2 | undefined): string | undefined {
  return s2?.legal_office_or_officer_incharge_contact_no ?? s2?.leagel_office_or_officer_incharge_contact_no;
}

function involvesGovernmentFromStep2(s2: CivilAppellateSaveStep2 | undefined): boolean | undefined {
  if (!s2) return undefined;
  if (s2.unincorporated_body_type_id != null) return Boolean(s2.unincorporated_body_type_id.trim());
  if ('unincorporated_body_type_id' in s2) return false;
  return undefined;
}

function involvesCorporationFromStep2(s2: CivilAppellateSaveStep2 | undefined): boolean | undefined {
  if (!s2) return undefined;
  if (s2.incorporated_body_type_id != null) return Boolean(s2.incorporated_body_type_id.trim());
  if ('incorporated_body_type_id' in s2) return false;
  return undefined;
}

function isIncorporatedBodyFromStep2(s2: CivilAppellateSaveStep2 | undefined): boolean | undefined {
  if (!s2) return undefined;
  if (typeof s2.is_incorporated_body === 'boolean') return s2.is_incorporated_body;
  if (involvesCorporationFromStep2(s2) === true) return true;
  if (involvesGovernmentFromStep2(s2) === true) return false;
  return undefined;
}

function apiDocumentTypeId(file: CivilAppellateSaveStep5File): string {
  return (file.document_type_id ?? file.document_id ?? '').trim();
}

function step5DocumentsFromApi(s5: CivilAppellateSaveStep5 | undefined): CivilAppellateSaveStep5File[] {
  return [
    ...(s5?.uploaded_files ?? []),
    ...(s5?.extra_uploaded_files ?? []),
    ...(s5?.documents ?? []),
  ];
}

function yesNoToBool(v: '' | 'yes' | 'no'): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

function boolToYesNo(v: boolean | undefined | null): '' | 'yes' | 'no' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

type SavedFileRow = {
  file_name?: string | null;
  original_file_name?: string | null;
  fileName?: string | null;
  originalFileName?: string | null;
  upload_time?: string | null;
  uploadTime?: string | null;
};

function savedFileFields(file: SavedFileRow): { fileName: string; originalFileName: string } {
  const apiFileName = file.file_name ?? file.fileName ?? '';
  const original = file.original_file_name ?? file.originalFileName;
  return normalizeDocumentFileFields(apiFileName, original);
}

function step5FileFields(file: CivilAppellateSaveStep5File): { fileName: string; originalFileName: string } {
  return savedFileFields(file);
}

function step5FileNamesForSave(
  fileName: string | null | undefined,
  originalFileName: string | null | undefined,
): { file_name?: string; original_file_name?: string } {
  const a = fileName?.trim() ?? '';
  const b = originalFileName?.trim() ?? '';
  if (!a && !b) return {};

  if (a && looksLikeStorageKey(a)) {
    return { file_name: a, ...(b ? { original_file_name: b } : {}) };
  }
  if (b && looksLikeStorageKey(b)) {
    return { file_name: b, ...(a ? { original_file_name: a } : {}) };
  }

  return { file_name: a, ...(b ? { original_file_name: b } : {}) };
}

function otherMethodFromStep4(s4: CivilAppellateSaveStep4 | undefined): string {
  const raw = s4?.other_method;
  return typeof raw === 'string' ? raw.trim() : '';
}

function otherMethodFromDeliveryRows(s4: CivilAppellateSaveStep4 | undefined): string {
  const raw = s4?.physical_file_delivery_methods;
  if (!raw?.length || typeof raw[0] === 'string') return '';

  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const name = item.delivery_method_name?.trim() ?? '';
    const other = item.other_method?.trim();
    if (name.toLowerCase() === 'other' && other) return other;
  }
  return '';
}

function resolveApplicableAreaIds(response: CivilAppellateStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { ids } = parseApplicableAreasFromApi(src);
    if (ids.length) return ids;
  }
  if (Array.isArray(response.applicableAreaIds) && response.applicableAreaIds.length) {
    return response.applicableAreaIds.filter((id) => typeof id === 'string' && id.trim());
  }
  return prev;
}

function resolveApplicableAreaNames(response: CivilAppellateStepsResponse, prev: string[]): string[] {
  for (const src of [response.applicable_areas]) {
    const { names } = parseApplicableAreasFromApi(src);
    if (names.length) return names;
  }
  const named = response.applicable_area_names ?? response.applicableAreaNames;
  if (Array.isArray(named) && named.length) {
    return named.filter((n): n is string => typeof n === 'string' && Boolean(n.trim()));
  }
  return prev;
}

function judgmentDocumentsFromApi(s1: CivilAppellateSaveStep1 | undefined): CivilAppellateJudgmentDocument[] {
  const raw = s1?.judgement_or_order_documents ?? s1?.judgment_or_order_documents ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((f) => {
      if (!f || typeof f !== 'object') return null;
      const { fileName, originalFileName } = savedFileFields(f as SavedFileRow);
      if (!fileName && !originalFileName) return null;
      const row = f as Record<string, unknown>;
      return {
        id:
          (typeof row.case_request_civil_appiliate_judgement_or_order_document_id === 'string'
            ? row.case_request_civil_appiliate_judgement_or_order_document_id
            : '') || crypto.randomUUID(),
        fileName,
        originalFileName,
        uploadTime: '',
      };
    })
    .filter((f): f is CivilAppellateJudgmentDocument => f !== null);
}

function mapStep1(draft: CivilAppellateWizardDraft): SaveStepFields<CivilAppellateSaveStep1> {
  const judgmentDocs = draft.judgmentOrOrderDocuments
    .filter((d) => strOrNull(d.fileName) || strOrNull(d.originalFileName))
    .map((d) => {
      const names = step5FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      return names;
    })
    .filter((d) => Object.keys(d).length);

  return {
    description: strOrNull(draft.description),
    sub_category_id: strOrNull(draft.subFileCategoryId),
    province_id: strOrNull(draft.provinceId),
    district_id: strOrNull(draft.districtId),
    is_appeal_matter: yesNoToBool(draft.isAppealMatter),
    originalCourt: draft.isAppealMatter === 'yes' ? strOrNull(draft.originalCourt) : undefined,
    judgment_or_order_documents: judgmentDocs.length ? judgmentDocs : undefined,
    external_case_reference_number: strOrNull(draft.yourReferenceNumber),
    other_external_case_reference_number: strOrNull(draft.otherExternalReferences),
    case_number: strOrNull(draft.caseNumber),
    has_connected_case: yesNoToBool(draft.hasConnectedMatters),
    connected_case_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedCaseNumber) : undefined,
    connected_agd_file_ref_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedAgdFileRef) : undefined,
  };
}

function mapStep2(draft: CivilAppellateWizardDraft): SaveStepFields<CivilAppellateSaveStep2> {
  return {
    plaintiff_applicant_name: strOrNull(draft.plaintiffName),
    defendant_respondant_name: strOrNull(draft.defendantName),
    is_incorporated_body: draft.isIncorporatedBody,
    other_involved_instruction: strOrNull(draft.otherInvolvedInstitutions),
    leagel_office_or_officer_incharge_name: strOrNull(draft.legalOfficerName),
    leagel_office_or_officer_incharge_contact_no: strOrNull(draft.legalOfficerContact),
  };
}

function mapStep3(draft: CivilAppellateWizardDraft): SaveStepFields<CivilAppellateSaveStep3> {
  return {
    urgency_level_id: strOrNull(draft.urgencyLevelId),
    deadline: strOrNull(draft.deadline),
    urgency_justification: strOrNull(draft.urgencyJustification),
  };
}

function mapStep4(draft: CivilAppellateWizardDraft): SaveStepFields<CivilAppellateSaveStep4> {
  const ids = draft.deliveryMethodIds.filter(Boolean);
  const step4: CivilAppellateSaveStep4 = {
    physical_file_delivery_method_ids: ids.length ? ids : undefined,
  };

  if (draftHasOtherDeliverySelected(draft)) {
    step4.other_method = strOrNull(draft.deliveryOther);
  }

  return step4;
}

function mapStep5(draft: CivilAppellateWizardDraft): SaveStepFields<CivilAppellateSaveStep5> {
  const uploaded_files = draft.documents
    .filter(
      (d) =>
        isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step5FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      const documentTypeId = resolveDocumentTypeUuid(d.documentTypeId, d.checklistItemId);
      return {
        ...(documentTypeId ? { document_type_id: documentTypeId } : {}),
        ...names,
        ...(reason ? { reason } : {}),
      };
    });
  const extra_uploaded_files = draft.documents
    .filter(
      (d) =>
        !isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (strOrNull(d.fileName) || strOrNull(d.skipReason)),
    )
    .map((d) => {
      const reason = strOrNull(d.skipReason);
      const names = step5FileNamesForSave(strOrNull(d.fileName), strOrNull(d.originalFileName));
      return {
        ...names,
        ...(reason ? { reason } : {}),
      };
    });

  return {
    ...(uploaded_files.length ? { uploaded_files } : {}),
    ...(extra_uploaded_files.length ? { extra_uploaded_files } : {}),
  };
}

export function buildSaveCivilAppellatePayload(
  draft: CivilAppellateWizardDraft,
  options: {
    currentStep?: CivilAppellateWizardStep;
    isDraft: boolean;
    confirmedCorrect?: boolean;
    allSteps?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): SaveCivilAppellateStepsPayload {
  const areaIds = draft.applicableAreaIds.filter(Boolean);
  const payload: SaveCivilAppellateStepsPayload = {
    is_draft_status: options.isDraft,
    ...buildInstituteApiPayload(draft),
  };
  const caseRequestId = caseRequestIdForSave(draft.draftRequestId);
  if (caseRequestId) payload.case_request_id = caseRequestId;

  if (options.isUpdateAlreadySubmitted) payload.is_update_already_submitted = true;
  if (options.confirmedCorrect != null) payload.confirmed_correct = options.confirmedCorrect;
  if (caseRequestId || areaIds.length) payload.applicable_areas = areaIds.length ? areaIds : null;

  if (options.allSteps) {
    payload.step_1 = mapStep1(draft);
    payload.step_2 = mapStep2(draft);
    payload.step_3 = mapStep3(draft);
    payload.step_4 = mapStep4(draft);
    payload.step_5 = mapStep5(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  const step = options.currentStep;
  if (!step) return finalizeCaseRequestSavePayload(payload, options);

  if (options.isDraft) {
    if (step >= 1) payload.step_1 = mapStep1(draft);
    if (step >= 2) payload.step_2 = mapStep2(draft);
    if (step >= 3) payload.step_3 = mapStep3(draft);
    if (step >= 4) payload.step_4 = mapStep4(draft);
    if (step >= 5) payload.step_5 = mapStep5(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  if (step === 1) payload.step_1 = mapStep1(draft);
  if (step === 2) payload.step_2 = mapStep2(draft);
  if (step === 3) payload.step_3 = mapStep3(draft);
  if (step === 4) payload.step_4 = mapStep4(draft);
  if (step === 5) payload.step_5 = mapStep5(draft);

  return finalizeCaseRequestSavePayload(payload, options);
}

export function mapCivilAppellateStepsToDraft(
  response: CivilAppellateStepsResponse,
  prev: CivilAppellateWizardDraft,
): CivilAppellateWizardDraft {
  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;

  const documents = (() => {
    const fromApi = step5DocumentsFromApi(s5);
    if (!fromApi.length) return prev.documents;
    if (!prev.documents.length) {
      return fromApi
        .filter((f) => {
          const typeId = apiDocumentTypeId(f);
          return typeId ? isDocumentTypeUuid(typeId) : Boolean((f.file_name ?? '').trim() || (f.reason ?? '').trim());
        })
        .map((f) => {
          const { fileName, originalFileName } = step5FileFields(f);
          const typeId = apiDocumentTypeId(f);
          return {
            id: (f.case_request_document_id ?? '').trim() || crypto.randomUUID(),
            checklistItemId: '',
            documentTypeId: typeId,
            documentName: 'Document',
            mandatory: false,
            fileName,
            originalFileName,
            uploadTime: '',
            skipReason: f.reason ?? '',
          };
        });
    }
    return prev.documents.map((doc) => {
      const match = fromApi.find((f) => {
        const typeId = apiDocumentTypeId(f);
        if (typeId && typeId === doc.documentTypeId) return true;
        return false;
      });
      if (!match) return doc;
      const { fileName, originalFileName } = step5FileFields(match);
      return {
        ...doc,
        documentTypeId: apiDocumentTypeId(match) || doc.documentTypeId,
        fileName: fileName || doc.fileName,
        originalFileName: originalFileName || doc.originalFileName,
        uploadTime: match.upload_time ?? doc.uploadTime,
        skipReason: match.reason ?? doc.skipReason,
      };
    });
  })();

  const delivery = parsePhysicalFileDeliveryMethods(s4?.physical_file_delivery_methods);
  const deliveryIds = s4?.physical_file_delivery_method_ids?.length
    ? s4.physical_file_delivery_method_ids
    : delivery.ids;

  const fileCategoryId =
    (response.file_category_id ?? s1?.category_id ?? prev.fileCategoryId) || CIVIL_APPELLATE_CATEGORY_ID;
  const fileCategoryName =
    (response.file_category_name ?? s1?.category_name ?? prev.fileCategory) || FILE_CATEGORY_CIVIL_APPELLATE;
  const subFileCategoryId =
    response.file_sub_category_id ?? s1?.sub_category_id ?? prev.subFileCategoryId;
  const subFileCategoryName =
    response.file_sub_category_name ?? s1?.sub_category_name ?? prev.subFileCategoryName;

  const isIncorporatedBody = isIncorporatedBodyFromStep2(s2);

  return {
    ...prev,
    ...mapInstituteFromApiResponse(s1 ?? {}, prev),
    draftRequestId: caseRequestIdFromResponse(response) || prev.draftRequestId,
    referenceNo: referenceNoFromResponse(response) ?? prev.referenceNo,
    fileCategory: fileCategoryName,
    fileCategoryId,
    subFileCategoryId,
    subFileCategoryName,
    applicableAreaIds: resolveApplicableAreaIds(response, prev.applicableAreaIds),
    applicableAreas: resolveApplicableAreaNames(response, prev.applicableAreas),
    description: s1?.description ?? prev.description,
    isAppealMatter: boolToYesNo(s1?.is_appeal_matter) || prev.isAppealMatter,
    originalCourt: s1?.original_court ?? s1?.originalCourt ?? prev.originalCourt,
    judgmentOrOrderDocuments: judgmentDocumentsFromApi(s1).length
      ? judgmentDocumentsFromApi(s1)
      : prev.judgmentOrOrderDocuments,
    provinceId: s1?.province_id ?? prev.provinceId,
    provinceName: s1?.province_name ?? prev.provinceName,
    districtId: s1?.district_id ?? prev.districtId,
    districtName: s1?.district_name ?? prev.districtName,
    yourReferenceNumber: s1?.external_case_reference_number ?? prev.yourReferenceNumber,
    otherExternalReferences:
      s1?.other_external_case_reference_number ?? prev.otherExternalReferences,
    caseNumber: s1?.case_number ?? prev.caseNumber,
    hasConnectedMatters: boolToYesNo(s1?.has_connected_case) || prev.hasConnectedMatters,
    connectedCaseNumber: s1?.connected_case_number ?? prev.connectedCaseNumber,
    connectedAgdFileRef: s1?.connected_agd_file_ref_number ?? prev.connectedAgdFileRef,
    plaintiffName: s2?.plaintiff_applicant_name ?? prev.plaintiffName,
    defendantName:
      s2?.defendant_respondant_name ?? s2?.defendant_respondent_name ?? prev.defendantName,
    isIncorporatedBody: isIncorporatedBody ?? prev.isIncorporatedBody,
    otherInvolvedInstitutions:
      s2?.other_involved_institutions ?? s2?.other_involved_instruction ?? prev.otherInvolvedInstitutions,
    legalOfficerName: legalOfficerNameFromStep2(s2) ?? prev.legalOfficerName,
    legalOfficerContact: legalOfficerContactFromStep2(s2) ?? prev.legalOfficerContact,
    urgencyLevelId: s3?.urgency_level_id ?? prev.urgencyLevelId,
    urgencyLevelName: s3?.urgency_level_name ?? prev.urgencyLevelName,
    deadline: s3?.deadline ?? prev.deadline,
    urgencyJustification: s3?.urgency_justification ?? prev.urgencyJustification,
    deliveryMethodIds: deliveryIds.length ? deliveryIds : prev.deliveryMethodIds,
    deliveryMethods: delivery.names.length ? delivery.names : prev.deliveryMethods,
    deliveryOther: otherMethodFromStep4(s4) || otherMethodFromDeliveryRows(s4) || prev.deliveryOther,
    documents,
  };
}
